import { useQuery } from "@tanstack/react-query";
import { Scale } from "lucide-react";
import { fetchRateDifferentials } from "@/lib/api";

function Spark({ data }) {
  if (!data || data.length < 2) return null;
  const w = 90, h = 22, pad = 2;
  const min = Math.min(...data), max = Math.max(...data);
  const range = max - min || 1;
  const step = (w - pad * 2) / (data.length - 1);
  const yOf = (v) => pad + (h - pad * 2) * (1 - (v - min) / range);
  const pts = data.map((v, i) => `${(pad + i * step).toFixed(1)},${yOf(v).toFixed(1)}`);
  const up = data[data.length - 1] >= data[0];
  return (
    <svg width={w} height={h} aria-hidden>
      <polyline points={pts.join(" ")} fill="none" stroke={up ? "#22C55E" : "#EF4444"} strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  );
}

export default function RateDifferentials() {
  const q = useQuery({
    queryKey: ["rate-differentials"],
    queryFn: fetchRateDifferentials,
    staleTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
  });
  const d = q.data;

  return (
    <div className="border border-zinc-800 bg-[#09090B]" data-testid="rate-differentials-card">
      <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-widest text-zinc-400 flex items-center gap-1.5">
          <Scale size={11} strokeWidth={1.5} className="text-amber-term" /> Rate Differentials · FX
        </span>
        {d && (
          <span className="text-[11px] uppercase tracking-widest text-zinc-600 tabular-nums" data-testid="rd-curve">
            US 2s10s{" "}
            <span className={d.us_2s10s_bps >= 0 ? "text-pos" : "text-neg"}>{d.us_2s10s_bps}bp</span>
            {d.us_2s10s_chg_bps != null && (
              <span className="text-zinc-500"> ({d.us_2s10s_chg_bps > 0 ? "+" : ""}{d.us_2s10s_chg_bps})</span>
            )}
          </span>
        )}
      </div>

      {q.isLoading ? (
        <div className="h-[120px] flex items-center justify-center text-zinc-600 text-[11px] uppercase tracking-widest">
          Loading differentials…
        </div>
      ) : q.isError ? (
        <div className="h-[120px] flex items-center justify-center text-neg text-[11px] uppercase tracking-widest">
          Differentials unavailable
        </div>
      ) : (
        <table className="w-full text-[11px]" data-testid="rate-differentials-table">
          <thead>
            <tr className="border-b border-zinc-800 text-[11px] uppercase tracking-widest text-zinc-500">
              <th className="px-3 py-1.5 text-left font-medium">Pair</th>
              <th className="px-2 py-1.5 text-left font-medium">10Y Gap (US−)</th>
              <th className="px-2 py-1.5 text-right font-medium">Δ 30D</th>
              <th className="px-2 py-1.5 text-center font-medium hidden sm:table-cell">90D</th>
              <th className="px-3 py-1.5 text-right font-medium">Read</th>
            </tr>
          </thead>
          <tbody>
            {d.pairs.map((p) => (
              <tr key={p.pair} className="border-b border-zinc-900 last:border-0" data-testid={`rd-row-${p.pair.replace("/", "")}`}>
                <td className="px-3 py-2 text-left font-semibold text-amber-term">{p.pair}</td>
                <td className="px-2 py-2 text-left tabular-nums">
                  <span className="text-zinc-100">{p.diff_10y != null ? `${p.diff_10y > 0 ? "+" : ""}${p.diff_10y}pp` : "—"}</span>
                  <span className="ml-1.5 text-[11px] uppercase tracking-widest text-zinc-600">{p.foreign}</span>
                  {p.freq === "monthly" && (
                    <span className="ml-1 text-[11px] uppercase tracking-widest text-zinc-600" title={`monthly source · as of ${p.as_of || "—"}`}>
                      · mo {p.as_of ? p.as_of.slice(0, 7) : ""}
                    </span>
                  )}
                </td>
                <td className={`px-2 py-2 text-right tabular-nums ${p.chg_bps > 0 ? "text-pos" : p.chg_bps < 0 ? "text-neg" : "text-zinc-500"}`}>
                  {p.chg_bps != null ? `${p.chg_bps > 0 ? "+" : ""}${p.chg_bps}bp` : "—"}
                </td>
                <td className="px-2 py-2 hidden sm:table-cell">
                  <div className="flex justify-center">{p.spark ? <Spark data={p.spark} /> : <span className="text-zinc-700">—</span>}</div>
                </td>
                <td className="px-3 py-2 text-right text-[11px] uppercase tracking-widest text-zinc-500">{p.implication}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="px-3 py-2 text-[11px] leading-relaxed text-zinc-600 border-t border-zinc-900">
        10Y nominal yield gap (US minus counterpart) — the primary rate driver of each pair. DE daily (ECB); UK/JP monthly (FRED). Wider gap generally supports USD.
      </p>
    </div>
  );
}
